import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { Line } from 'react-chartjs-2';

const LazyChart = forwardRef(({ data, options, height = 300 }, innerRef) => {
  const containerRef = useRef(null); // グラフを囲むdivへの参照
  const [hasChartBeenCreated, setHasChartBeenCreated] = useState(false); // グラフが一度でも構築されたかどうかのフラグ
  const [forceRenderAll, setForceRenderAll] = useState(false); // 印刷モードかどうかのフラグ

  // --- 印刷モードの検知 ---
  useEffect(() => {
    const mediaQueryList = window.matchMedia('print');
    
    const handler = (e) => {
      setForceRenderAll(e.matches);
    };
    
    mediaQueryList.addEventListener('change', handler);
    
    // コンポーネントがマウントされた時点で初期判定を行う
    if (mediaQueryList.matches) {
      setForceRenderAll(true);
    }
    
    // クリーンアップ関数
    return () => mediaQueryList.removeEventListener('change', handler);
  }, []);

  // --- Intersection Observer による初回表示の検知 ---
  useEffect(() => {
    // 既にグラフが作成されている、または印刷モードの場合は、オブザーバーは不要
    if (hasChartBeenCreated || forceRenderAll) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // グラフがビューポートに表示されたら
        if (entry.isIntersecting) {
          setHasChartBeenCreated(true); // グラフ作成フラグを立てる
          observer.disconnect(); // 一度作成したらオブザーバーは停止
        }
      },
      { threshold: 0.1 } // グラフ要素の10%が見えたら発火
    );

    // 監視対象のDOM要素が利用可能になったら監視を開始
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    // クリーンアップ関数: コンポーネントがアンマウントされるか、依存配列が変更されるときに実行
    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
      observer.disconnect();
    };
  }, [hasChartBeenCreated, forceRenderAll]);

  // --- グラフをレンダリングすべきかどうかの最終判定 ---
  // `true`の場合、LineコンポーネントはDOMに残り続ける
  // `false`の場合、Lineコンポーネント自体がDOMから削除される
  const shouldRenderChartComponent = hasChartBeenCreated || forceRenderAll;

  return (
    <div 
      ref={containerRef} 
      style={{ 
        height, 
        // グラフが非表示の場合でもスペースを確保しつつ見えなくする
        // DOMから要素自体を削除するのではなく、表示を切り替える
        visibility: shouldRenderChartComponent ? 'visible' : 'hidden', 
        width: '100%', // 幅も設定
        // backgroundColor: '#fff' // グラフがない場合の背景色は、不要であれば削除
      }}
    >
      {/* グラフが一度でも作成される必要がある場合にのみ、Lineコンポーネントをマウント */}
      {shouldRenderChartComponent && (
        <Line
          key={forceRenderAll ? 'print' : 'normal'} 
          ref={innerRef} // 親から渡されたrefをLineコンポーネントに直接渡す
          data={data}
          options={options}
          height={height}
          plugins={[]} // options.plugins で設定するため、ここでは空の配列を渡す
        />
      )}
      {/* 非表示時の空のdivも不要なので削除 */}
    </div>
  );
});

export default LazyChart;